import React, { useState, useEffect, useCallback } from "react";
import { v4 } from "uuid";
import Alerts from "../../molecules/Alerts";
import "./History.css";
import { Input } from "reactstrap";
import { getMyRequests } from "../../../utils/queries";
import { trackPromise } from "react-promise-tracker";
import { useNavigate } from "react-router-dom";
import RequestCard, { getEmployeeName } from "../../molecules/RequestCard/RequestCard";
import RequestListRow from "../../molecules/RequestCard/RequestListRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTh, faList } from "@fortawesome/free-solid-svg-icons";

// Hard-coded so the filter always offers every status, not only those present in the loaded data.
const statusList = [
  "All statuses",
  "Approved by finance",
  "Approved by line manager",
  "Cancelled",
  "Completed",
  "Form requested",
  "Form resubmitted",
  "Form submitted",
  "Rejected by finance",
  "Rejected by line manager",
  "Reopen",
  "Send back by requestor",
  "Validated by requestor",
];

const History = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [accountList, setAccountList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("viewMode") ?? "list");

  const addAlert = useCallback((message, color) => {
    setAlerts((prev) => [...prev, { message, id: v4(), color }]);
  }, []);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("viewMode", mode);
  };

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const result = await trackPromise(getMyRequests());
        setAccountList(result.data);
      } catch (error) {
        addAlert(error?.message || "Failed to load requests.", "error");
      } finally {
        setLoaded(true);
      }
    };
    loadAccounts();
  }, [addAlert]);

  const filterLogic = (account) => {
    const query = nameFilter.trim().toLowerCase();
    const matchesStatus =
      !selectedStatus || selectedStatus === "All statuses" || account.status === selectedStatus;

    const matchesSearch =
      !query ||
      String(account.id).includes(query) ||
      getEmployeeName(account).toLowerCase().includes(query) ||
      (account.requestContactEmail || "").toLowerCase().includes(query) ||
      account.token?.toLowerCase() === query;

    return matchesSearch && matchesStatus;
  };

  const filtered = accountList.filter(filterLogic);

  return (
    <div className="hy-page">
      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <div className="hy-toolbar">
        <div className="hy-toolbar__left">
          <div className="hy-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="hy-search-icon" />
            <Input
              type="search"
              className="hy-search"
              placeholder="Search..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="hy-toolbar__right">
          <select
            className="hy-status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {statusList.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <div className="hy-view-toggle">
            <button
              className={"hy-view-btn" + (viewMode === "card" ? " hy-view-btn--active" : "")}
              onClick={() => changeViewMode("card")}
              title="Card view"
            >
              <FontAwesomeIcon icon={faTh} />
            </button>
            <button
              className={"hy-view-btn" + (viewMode === "list" ? " hy-view-btn--active" : "")}
              onClick={() => changeViewMode("list")}
              title="List view"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        </div>
      </div>

      {!loaded ? null : filtered.length === 0 ? (
        <div className="hy-empty">No requests found</div>
      ) : viewMode === "card" ? (
        <div className="hy-card-grid">
          {filtered.map((account) => (
            <RequestCard
              key={account.id}
              account={account}
              href={`/history/${account.id}`}
              onClick={() => navigate(`/history/${account.id}`)}
            />
          ))}
        </div>
      ) : (
        <div className="hy-list">
          <div className="hy-list__header">
            <span className="hy-list__col hy-list__col--id">Id</span>
            <span className="hy-list__col hy-list__col--employee">Employee</span>
            <span className="hy-list__col hy-list__col--email">Email</span>
            <span className="hy-list__col hy-list__col--lm">Line Manager</span>
            <span className="hy-list__col hy-list__col--finance">Finance Approver</span>
            <span className="hy-list__col hy-list__col--status">Status</span>
            <span className="hy-list__col hy-list__col--date">Submitted</span>
          </div>
          {filtered.map((account) => (
            <RequestListRow
              key={account.id}
              account={account}
              href={`/history/${account.id}`}
              onClick={() => navigate(`/history/${account.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default History;
